import * as React from "react";
import { Modal, Typography, Card, Box, Grid, IconButton, Icon, Button, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CancelIcon from "../../../icons/cancelIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../../shared/sagas/auth/authSlice";
import { getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState } from "../../../shared/sagas/propertyGroups/propertyGroupsSlice";
import { Role } from "@inosio/payments-sdk";

const useStyles = makeStyles({
  modalContainer: {
    background: "#FFFFFF",
    boxShadow: "none",
    borderRadius: "24px",
  },
  titleText: {
    fontFamily: "Work Sans !important",
    fontStyle: "normal !important",
    fontWeight: "600 !important",
    fontSize: "18px !important",
    lineHeight: "22px !important",
    color: "#000000 !important",
    textAlign: "center",
  },
  buttonContainer: {
    position: "absolute",
    bottom: "20px",
    left: "24px",
    right: "24px",
    display: "flex",
    justifyContent: "flex-end",
  },
  labelLengthText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '14px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
  },
});

interface MaintenancePoliciesModalProps {
  open: boolean;
  description?: string;
  closeCallback: () => void;
  saveCallback: (text: string) => void;
  showSpinnerModal: () => void;
}

export default function MaintenancePoliciesModal(props: MaintenancePoliciesModalProps) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "16px",
    minHeight: '523px',
    pointerEvents: "auto",
    width: '98%',
    maxWidth: '472px',
    borderRadius: '24px',
    outline: 'none',
  };

  const currentUser = useSelector(selectCurrentUser);
  const { selectedPropertyGroupAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const [description, setDescription] = React.useState(props.description ?? '');
  const [descriptionError, setDescriptionError] = React.useState('');
  const [descriptionLengthLeft, setDescriptionLengthLeft] = React.useState(5000);

  React.useEffect(() => {
    setDescriptionLengthLeft(5000 - description.length);
  }, [description]);

  const handleSave = () => {
    setDescriptionError('');

    if (!description) setDescriptionError('Description is required.');
    if (description.length > 5000) setDescriptionError('The maximum length is 5000.');

    if (description && description.length <= 5000) {
      props.showSpinnerModal();

      dispatch(getUpdatePropertyGroupConfigs({ 
        propertyGroupUuid: selectedPropertyGroupAssignedConfigs.Uuid,
        productTypeId: 3,
        updatedPmConfig: {
          MaintenancePolicyText: description,
        },
      }));

      props.saveCallback(description);
    }
  }

  return (
    <div className={`${classes.modalContainer}`}>
      <Modal
        open={props.open}
        onClose={() => props.closeCallback()}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <Card sx={style}>
          <Grid container>
            <Grid item xs={2}></Grid>
            <Grid item xs={8}>
              <Typography className={classes.titleText} sx={{ mt: 1 }}>
                {currentUser?.currentProfile.role === Role.MaintenanceStaff ? 'View' : 'Describe'} Maintenance Policies
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <IconButton sx={{ ml: "30px" }} onClick={() => props.closeCallback()}>
                <Icon>
                  <img src={CancelIcon} alt="" width={24} height={24} />
                </Icon>
              </IconButton>
            </Grid>
          </Grid>

          <Box>
            <Grid
              item
              sx={{
                '& fieldset': {
                  borderRadius: '24px',
                  borderColor: 'white',
                },
                mx: 0.5,
                mt: 2,
              }}
            >
              <TextField
                multiline
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={14}
                sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px' }}
                placeholder={'Maintenance Policies'}
                error={!!descriptionError}
                inputProps={{ maxLength: 5000 }}
                disabled={currentUser?.currentProfile.role=== Role.MaintenanceStaff}
              />
              <Typography className={classes.labelLengthText} sx={{ textAlign: 'right', color: descriptionLengthLeft < 0 ? '#DB3939 !important' : '#14142B !important' }}>{`${descriptionLengthLeft} characters left.`}</Typography>
            </Grid>

            {currentUser?.currentProfile.role !== Role.MaintenanceStaff && (
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  style={{
                    fontFamily: "'Open Sans', sans-serif",
                    fontStyle: "normal",
                    fontWeight: 700,
                    width: "87px",
                    marginLeft: "8px",
                    background: "#39A2DB",
                    borderRadius: "24px",
                    textTransform: "capitalize",
                  }}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>              
            )}
          </Box>
        </Card>
      </Modal>
    </div>
  );
}
